/**
 * pwa-install.ts — Cài eCDP về máy (PWA): bắt sự kiện cài của trình duyệt, nhận biết nền tảng, nhớ "để sau".
 *
 * Trình duyệt Chromium (Chrome / Edge / Samsung Internet, Android + máy tính) bắn `beforeinstallprompt` MỘT lần, rất sớm
 * (có khi trước khi React dựng xong) → phải bắt ở main.tsx qua `captureInstallPrompt()`, giữ lại để bấm nút mới gọi `prompt()`.
 * Safari iPhone/iPad và Firefox KHÔNG có sự kiện này: iOS chỉ cài được qua Chia sẻ → "Thêm vào MH chính" (hộp hướng dẫn),
 * Firefox máy tính không cài được. Các hàm thuần (nền tảng, "để sau") tách riêng để test được.
 */
import { useSyncExternalStore } from 'react'

/** Sự kiện cài của Chromium (chưa có trong lib.dom). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

export type InstallPlatform = 'ios' | 'android' | 'desktop'

/** iPhone / iPad (kể cả iPadOS 13+ giả danh Mac: UA là Macintosh nhưng có màn hình cảm ứng). */
export function detectPlatform(ua: string, maxTouchPoints = 0): InstallPlatform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

/** Đang chạy như ứng dụng đã cài (cửa sổ riêng, không thanh địa chỉ). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true
    if (window.matchMedia?.('(display-mode: minimal-ui)').matches) return true
  } catch { /* bỏ qua */ }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

// ── "Để sau": nhớ theo THIẾT BỊ (quyền thông báo / cài app / nhắc TOTP đều gắn với trình duyệt, không
//    gắn với người dùng — cũng là nơi dùng chung cho mọi "lời mời nổi" vào web, không riêng PWA) ──

export type PromptKey = 'install' | 'notify' | 'totp'
const SNOOZE_KEY = (k: PromptKey) => `ecdp:prompt-snooze:${k}`
/** `totp` = 7 ngày — "hàng tuần" nhắc chủ sở hữu bật xác thực 2 lớp nếu vẫn chưa bật. */
export const SNOOZE_DAYS: Record<PromptKey, number> = { install: 7, notify: 3, totp: 7 }
const DAY_MS = 86_400_000

type Store = Pick<Storage, 'getItem' | 'setItem'>
function safeLocal(): Store | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}

export function snooze(key: PromptKey, days = SNOOZE_DAYS[key], now = Date.now(), store: Store | null = safeLocal()) {
  try { store?.setItem(SNOOZE_KEY(key), String(now + days * DAY_MS)) } catch { /* chế độ riêng tư */ }
}

/** Còn trong thời gian "để sau" không. Giá trị hỏng / không đọc được → coi như chưa hoãn. */
export function isSnoozed(key: PromptKey, now = Date.now(), store: Store | null = safeLocal()): boolean {
  try {
    const until = Number(store?.getItem(SNOOZE_KEY(key)))
    return Number.isFinite(until) && until > now
  } catch { return false }
}

// ── Kho nhỏ giữ sự kiện cài (ngoài React để bắt được sớm) ──

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
let captured = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

/** Gọi MỘT lần ở main.tsx. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === 'undefined') return
  captured = true
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault() // chặn thanh "Cài đặt" mặc định của Chrome — ta tự hiện ở chỗ phù hợp
    deferred = e as BeforeInstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => {
    installed = true
    deferred = null
    emit()
  })
}

export type InstallResult = 'accepted' | 'dismissed' | 'unavailable'

/** Mở hộp cài của trình duyệt (chỉ khi đã bắt được sự kiện). Sự kiện chỉ dùng được một lần. */
export async function promptInstall(): Promise<InstallResult> {
  const ev = deferred
  if (!ev) return 'unavailable'
  deferred = null
  emit()
  try {
    await ev.prompt()
    const { outcome } = await ev.userChoice
    if (outcome === 'accepted') { installed = true; emit() }
    return outcome
  } catch {
    return 'unavailable'
  }
}

export interface InstallState {
  /** Đã cài / đang chạy như ứng dụng → ẩn mọi lời mời. */
  installed: boolean
  /** Trình duyệt đã sẵn sàng cài bằng một chạm (Chromium). */
  canPromptNative: boolean
  platform: InstallPlatform
  /** Có đường cài để mời: cài một chạm, hoặc iOS (có hướng dẫn thủ công). Firefox máy tính / Safari Mac → không mời. */
  canOffer: boolean
}

let snapshotCache: InstallState | null = null
function snapshot(): InstallState {
  const platform = typeof navigator === 'undefined' ? 'desktop' : detectPlatform(navigator.userAgent, navigator.maxTouchPoints)
  const isInstalled = installed || isStandalone()
  const canPromptNative = deferred !== null
  const next: InstallState = { installed: isInstalled, canPromptNative, platform, canOffer: !isInstalled && (canPromptNative || platform === 'ios') }
  // useSyncExternalStore cần cùng tham chiếu khi không đổi, nếu không sẽ render vô hạn.
  if (snapshotCache && snapshotCache.installed === next.installed && snapshotCache.canPromptNative === next.canPromptNative
    && snapshotCache.platform === next.platform && snapshotCache.canOffer === next.canOffer) return snapshotCache
  snapshotCache = next
  return next
}

const SERVER_STATE: InstallState = { installed: false, canPromptNative: false, platform: 'desktop', canOffer: false }

export function useInstallState(): InstallState {
  return useSyncExternalStore(subscribe, snapshot, () => SERVER_STATE)
}

/** Chỉ cho test: đưa kho về trạng thái ban đầu. */
export function __resetInstallForTest() {
  deferred = null
  installed = false
  captured = false
  snapshotCache = null
  listeners.clear()
}
